/** Tile size in logical pixels. */
export const T = 16;

/**
 * Tile legend. Maps are grids of these characters; every visual and collision
 * rule for the ground layer derives from this table.
 */
export const TILE = {
  VOID: ' ',
  WOOD: '.',
  SCHOOL_FLOOR: ',',
  CARPET_RED: ':',
  CARPET_GREEN: ';',
  KITCHEN: 'k',
  BATH: 'b',
  WALL: '#',
  DOORWAY: 'd',
  DOOR: 'D',
  GRASS: 'g',
  FLOWERS: 'G',
  FIELD: 'F',
  TRACK: 't',
  PATH: 'p',
  PAVEMENT: 's',
  ROAD: 'r',
  ROAD_LINE: 'l',
  CROSSWALK: '=',
  WATER: '~',
  SAND: 'S',
  HEDGE: 'h',
  FENCE: 'f',
  ROOF_RED: 'R',
  ROOF_BLUE: 'U',
  ROOF_GREEN: 'Y',
  WALL_PLASTER: 'B',
  WALL_BRICK: 'K',
  WINDOW_PLASTER: 'w',
  WINDOW_BRICK: 'v',
  AWNING: 'A',
  COURT: 'c',
  TERRAZZO: 'z',
  MARKET: 'n',
  // Trips: Taipei, MRT, museum, Hualien.
  GRANITE: 'X',
  BRICK_WALK: 'Q',
  GLASS: 'J',
  FLAT_ROOF: 'O',
  STATION_FLOOR: 'm',
  TACTILE: 'e',
  RAILS: 'T',
  MARBLE_FLOOR: 'u',
  CLIFF: 'M',
  RIVER: 'q',
  BRIDGE: 'y',
  PEBBLES: 'o',
  SEA: 'Z',
  // Phase E district packages own these chars (ground in src/render/ground<Pkg>.ts):
  // xinyi '1' '2' '3'*, ximen '4' '5' '6'*, cks '7' '8' '9'*, hlcity 'a' 'i' 'j'*, taroko 'x' '!' '?'* (* solid).
  // Phase F: kaohsiung '@' '$' '%'*, tainan '&' '*' '^'*, barcelona '<' '>' '|'*.
} as const;

export type TileChar = (typeof TILE)[keyof typeof TILE];

const SOLID = new Set<string>([
  TILE.VOID, TILE.WALL, TILE.WATER, TILE.HEDGE, TILE.FENCE,
  TILE.ROOF_RED, TILE.ROOF_BLUE, TILE.ROOF_GREEN, 'P',
  TILE.WALL_PLASTER, TILE.WALL_BRICK, TILE.WINDOW_PLASTER, TILE.WINDOW_BRICK, TILE.AWNING,
  TILE.GLASS, TILE.FLAT_ROOF, TILE.RAILS, TILE.CLIFF, TILE.RIVER, TILE.SEA,
  // Phase E: the solid char of each district package (xinyi, ximen, cks, hlcity, taroko).
  '3', '6', '9', 'j', '?',
  '%', '^', '|',
]);

export function isSolidTile(ch: string): boolean {
  return SOLID.has(ch);
}

export const ROOFS = new Set<string>([TILE.ROOF_RED, TILE.ROOF_BLUE, TILE.ROOF_GREEN, TILE.FLAT_ROOF]);
export const BUILDING_WALLS = new Set<string>([TILE.WALL_PLASTER, TILE.WALL_BRICK, TILE.WINDOW_PLASTER, TILE.WINDOW_BRICK, TILE.DOOR, TILE.AWNING, TILE.GLASS]);
export const FLOORS = new Set<string>([TILE.WOOD, TILE.SCHOOL_FLOOR, TILE.CARPET_RED, TILE.CARPET_GREEN, TILE.KITCHEN, TILE.BATH, TILE.DOORWAY, TILE.TERRAZZO, TILE.STATION_FLOOR, TILE.TACTILE, TILE.MARBLE_FLOOR]);
export const GREENS = new Set<string>([TILE.GRASS, TILE.FLOWERS, TILE.FIELD]);
/** Pool water inside buildings (walkable? no — swimming is an activity). */
export const POOL = 'P';
export const ROADS = new Set<string>([TILE.ROAD, TILE.ROAD_LINE, TILE.CROSSWALK]);
